import {observer} from 'mobx-react-lite';
import {getStatus, OrderStatus} from '../../../../entity/enums/order-status';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useService} from '../../../../infrastructure/service/service';
import {TitleDialogProps} from '../../../../entity/props/title/title-dialog-props';
import React from 'react';
import {Section} from '../../../../entity/resource/section';
import {toast} from 'react-toastify';
import {router} from '../../../router/Routes';
import {ReviewStatus} from '../../../../entity/enums/review-status';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {TitleDialogDto} from '../../../../entity/dto/title-dialog-dto';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/title-dialog.css';

const TitleDialog = (props: TitleDialogProps): JSX.Element => {
  const {titleService} = useService();

  const handleSubmit = async (values: TitleDialogDto, {setSubmitting}: FormikHelpers<TitleDialogDto>): Promise<void> => {
    const documentCount = titleService.getTitle()?.sections.reduce((total: number, section: Section): number => total + section.documents.length, 0);

    if (documentCount === 0) {
      props.setOpen(false);
      toast.warning('You cannot submit empty title !!');
    } else {
      setSubmitting(true);

      let response;
      if (props.title.status === getStatus(OrderStatus.OPEN))
        response = await titleService.submitTitle(props.title.createdByOrganizationID, props.title.id, values.comment);
      else if (props.title.status === getStatus(OrderStatus.PENDING_REVIEW))
        response = await titleService.reviewTitle(props.title.createdByOrganizationID, props.title.id, values.comment, values.reviewStatus === ReviewStatus.APPROVE ? ReviewStatus.APPROVE : ReviewStatus.REQUEST_CHANGES);
      else
        response = await titleService.approveTitle(props.title.createdByOrganizationID, props.title.id, values.comment, values.reviewStatus === ReviewStatus.APPROVE ? ReviewStatus.APPROVE : ReviewStatus.REQUEST_CHANGES);

      if (response)
        titleService.setTitle(response);

      setSubmitting(false);
      props.setOpen(false);
      await router.navigate('/dashboard');
    }
  }

  return (
    <Dialog open={props.open}>
      <DialogTitle className={'zitles-dialog-title'}>
        <span>
          {
            props.title.status === getStatus(OrderStatus.OPEN)
              ? 'Review Confirm'
              : props.title.status === getStatus(OrderStatus.PENDING_REVIEW)
                ? 'Review'
                : 'Approve'
          }
        </span>
        <IconButton id={'btn-title-dialog-close'} onClick={(): void => props.setOpen(false)}>
          <CloseIcon className={'dialog-icon'}/>
        </IconButton>
      </DialogTitle>
      <DialogContent className={'title-dialog-content'} dividers>
        <Formik
          initialValues={titleService.getTitleDialogDto()}
          onSubmit={handleSubmit}>
          {({values, handleChange, handleSubmit, isSubmitting}: FormikProps<TitleDialogDto>): JSX.Element => (
            <Form onSubmit={handleSubmit}>
              {
                props.title.status === getStatus(OrderStatus.OPEN) &&
                  <Typography className={'title-dialog-description'}>
                    Are you sure you want to submit this Title for Review? You won't be able to make edits while the Title is being reviewed.
                  </Typography>
              }
              <TextField
                id={'txt-title-dialog-comment'}
                fullWidth
                multiline
                rows={5}
                label='Comment'
                name={'comment'}
                onChange={handleChange}
                value={values.comment}
                className={'zitles-textarea'}
                placeholder='Comments are visible to all users in all organizations associated with this title order.'/>
              {
                props.title.status !== getStatus(OrderStatus.OPEN) &&
                  <FormControl component='fieldset' className={'radio-button-group'}>
                    <RadioGroup
                      id={'radio-group-title-dialog-review-status'}
                      name="reviewStatus"
                      value={values.reviewStatus}
                      onChange={handleChange}>
                      <FormControlLabel
                        id={'radio-label-title-dialog-review-status-approve'}
                        className={`radio-button ${values.reviewStatus !== ReviewStatus.APPROVE ? 'inactive' : ''}`}
                        value={ReviewStatus.APPROVE}
                        control={<Radio className={'radio-button'}/>}
                        label="Approve"/>
                      <Typography className={`radio-button-description ${values.reviewStatus !== ReviewStatus.APPROVE ? 'inactive' : ''}`}>Complete this title order and submit to client</Typography>
                      <FormControlLabel
                        id={'radio-label-title-dialog-review-status-request-changes'}
                        className={`radio-button ${values.reviewStatus !== ReviewStatus.REQUEST_CHANGES ? 'inactive' : ''}`}
                        value={ReviewStatus.REQUEST_CHANGES}
                        control={<Radio className={'radio-button'}/>}
                        label="Request Changes"/>
                      <Typography className={`radio-button-description ${values.reviewStatus !== ReviewStatus.REQUEST_CHANGES ? 'inactive' : ''}`}>Submit feedback that must be addressed</Typography>
                    </RadioGroup>
                  </FormControl>
              }
              <Divider sx={{ my: 2, mx: -3 }} />
              <Box className={'action-btn-container'}>
                <Button
                  id={'btn-title-dialog-cancel'}
                  disableTouchRipple
                  onClick={(): void => props.setOpen(false)}
                  className={'zitles-button cancel'}>
                  Cancel
                </Button>
                <Button
                  id={'btn-title-dialog-submit'}
                  type='submit'
                  disabled={isSubmitting}
                  className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                  startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                  {
                    isSubmitting ? 'Submitting' : 'Submit'
                  }
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default observer(TitleDialog);
